<template>
    <div class="tw:text-surface-500 tw:space-x-2">
        <i class="pi pi-briefcase tw:inline-block" />
        <span class="tw:text-sm tw:uppercase">Course Resources</span>
    </div>
    <Tree
        v-model:expanded-keys="expandedKeys"
        class="tree"
        selection-mode="single"
        :value="courseResourcesTreeNodes"
        :pt="{
            node: {
                onFocus: showNavTitleTooltipOnFocus,
                onBlur: hideNavTitleTooltipOnBlur,
            },
            nodeContent: {
                class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer tw:w-full tw:flex tw:items-center tw:min-w-0'
            },
            nodeChildren: {
                style: 'padding-inline-start: 1rem !important;'
            },
            nodeToggleButton: {
                class: 'tw:order-last tw:ml-auto!'
            },
            nodeLabel: {
                class: 'tw:flex-1 tw:min-w-0',
            },
        }"
        @node-select="onNodeSelect"
    >
        <template #default="{ node }">
            <!-- Render dynamic component ONLY for leaf nodes (no children) with a type -->
            <component
                :is="getComponentForItem(node.data)"
                v-if="node.data && getComponentForItem(node.data) && !node.children"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0 tw:overflow-hidden"
                :item="node.data"
            />

            <!-- For nodes with children and a type, just render the label (Tree handles expansion) -->
            <span
                v-else-if="node.data && node.children"
                class="tw:flex tw:items-center tw:min-w-0 tw:w-full tw:overflow-hidden"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.text ?? node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.text ?? node.label }}</span>
            </span>

            <!-- Render router-link if node has a route -->
            <router-link
                v-else-if="node.route"
                :to="node.route"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0 tw:overflow-hidden"
                :class="{ 'is-active': node.isActive }"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.label }}</span>
            </router-link>

            <!-- Fallback: just render the label -->
            <span
                v-else
                v-tooltip.right.hover="navTitleTooltip(node.label)"
                class="nav-title-clamp-2 tw:min-w-0"
            >{{ node.label }}</span>
        </template>
    </Tree>

    <div class="tw:text-surface-500 tw:space-x-2">
        <i class="pi pi-graduation-cap tw:inline-block" />
        <span class="tw:text-sm tw:uppercase">Course Content</span>
    </div>

    <Tree
        v-model:expanded-keys="expandedKeys"
        class="tree"
        selection-mode="single"
        :value="treeNodes"
        :pt="{
            node: {
                onFocus: showNavTitleTooltipOnFocus,
                onBlur: hideNavTitleTooltipOnBlur,
            },
            nodeContent: {
                class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer tw:w-full tw:flex tw:items-center tw:min-w-0'
            },
            nodeChildren: {
                style: 'padding-inline-start: 1rem !important;'
            },
            nodeToggleButton: {
                class: 'tw:order-last tw:ml-auto!'
            },
            nodeLabel: {
                class: 'tw:flex-1 tw:min-w-0',
            },
        }"
        @node-select="onNodeSelect"
    >
        <template #default="{ node }">
            <!-- Render dynamic component ONLY for leaf nodes (no children) with a type -->
            <component
                :is="getComponentForItem(node.data)"
                v-if="node.data && getComponentForItem(node.data) && !node.children"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0 tw:overflow-hidden"
                :item="node.data"
            />

            <!-- For nodes with children and a type, just render the label (Tree handles expansion) -->
            <span
                v-else-if="node.data && node.children"
                class="tw:flex tw:items-center tw:min-w-0 tw:w-full tw:overflow-hidden"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.text ?? node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.text ?? node.label }}</span>
            </span>

            <!-- Render router-link if node has a route -->
            <router-link
                v-else-if="node.route"
                :to="node.route"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0 tw:overflow-hidden"
                :class="{ 'is-active': node.isActive }"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.label }}</span>
            </router-link>

            <!-- Fallback: just render the label -->
            <span
                v-else
                v-tooltip.right.hover="navTitleTooltip(node.label)"
                class="nav-title-clamp-2 tw:min-w-0"
            >{{ node.label }}</span>
        </template>
    </Tree>
</template>

<script>
import { pascalCase } from 'change-case';
import get from 'lodash/get.js';
import { mapState } from 'vuex';

import * as NestedListItems from '@/components/nested-list/nested-list-item/Index.js';
import MenuLink from '@/components/nested-list/nested-list-item/MenuLink.vue';
import CourseTemplate from '@/models/CourseTemplate.js';
import { defaultMenu } from '@/store/modules/Menu.js';
import {
    hideNavTitleTooltipOnBlur,
    navTitleTooltip,
    showNavTitleTooltipOnFocus,
} from '@/utilities/longTitleTooltip.js';

export default {
    name: 'CourseTemplateMenu',

    components: {
        MenuLink,
        ...NestedListItems.default,
    },

    props: {
        items: {
            type: Object,
            default: () => defaultMenu,
        },

        courseTemplateId: {
            type: [Number, String],
            required: true,
        },

        itemToOpen: {
            type: String,
            default: undefined,
        },
    },

    data: () => ({
        expandedKeys: {},
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        courseTemplate() {
            return CourseTemplate.query().with(['groups']).find(this.courseTemplateId);
        },

        canViewCourseTemplate() {
            return (
                this.user &&
                this.courseTemplateId &&
                (this.user.hasPermissionViaCourseTo(this.courseTemplate, 'view a course') || this.user.is_admin)
            );
        },

        hasWeeks() {
            return (
                this.user &&
                this.courseTemplateId &&
                (this.user.hasPermissionViaCourseTo(this.courseTemplate, 'view a course') || this.user.is_admin) &&
                this.weeks('past')
            );
        },

        courseResourcesTreeNodes() {
            const nodes = [];

            // About This Course Template
            const aboutChildren = [];

            if (this.courseTemplateId && this.$gate.allows('viewTemplatePreview', ['Gradebook', this.courseTemplate])) {
                aboutChildren.push({
                    key: 'gradebook-preview',
                    label: 'Gradebook Preview',
                    isActive: this.$route.name === 'course-template-gradebook-preview',
                    route: {
                        name: 'course-template-gradebook-preview',
                        params: { id: this.courseTemplateId },
                    },
                });
            }

            if (this.canViewCourseTemplate && this.items.meta && this.items.meta.length) {
                const metaChildren = this.convertNestedListToTreeNodes(this.items.meta, 'meta');
                if (metaChildren.length > 0) {
                    aboutChildren.push({
                        key: 'meta',
                        label: 'Meta',
                        children: metaChildren,
                    });
                }
            }

            if (aboutChildren.length > 0) {
                nodes.push({
                    key: 'about-course-template',
                    label: 'About This Course Template',
                    children: aboutChildren,
                });
            }

            return nodes;
        },

        treeNodes() {
            const nodes = [];

            // Course Content (misc_pre)
            if (
                this.user &&
                this.items.curriculum &&
                this.items.curriculum.misc_pre &&
                this.items.curriculum.misc_pre.length
            ) {
                const courseContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_pre,
                    'course-content'
                );
                if (courseContentChildren.length > 0) {
                    nodes.push({
                        key: 'course-content',
                        label: 'Course Content',
                        children: courseContentChildren,
                    });
                }
            }

            // Previous Weeks
            if (this.hasWeeks) {
                const previousWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.past,
                    'previous-weeks'
                );
                if (previousWeeksChildren.length > 0) {
                    nodes.push({
                        key: 'previous-weeks',
                        label: 'Previous Weeks',
                        children: previousWeeksChildren,
                    });
                }
            }

            // Upcoming Weeks (course templates are not launched — all weeks are upcoming)
            const upcomingChildren = [];

            if (this.canViewCourseTemplate && this.weeks('future')) {
                const futureWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.future,
                    'future-weeks'
                );
                if (futureWeeksChildren.length > 0) {
                    futureWeeksChildren.forEach(item => {
                        upcomingChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            if (
                this.canViewCourseTemplate &&
                this.items.curriculum &&
                this.items.curriculum.misc_post &&
                this.items.curriculum.misc_post.length
            ) {
                const additionalContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_post,
                    'additional-content'
                );
                if (additionalContentChildren.length > 0) {
                    upcomingChildren.push({
                        key: 'additional-content',
                        label: 'Additional Content',
                        children: additionalContentChildren,
                    });
                }
            }

            if (upcomingChildren.length > 0) {
                nodes.push({
                    key: 'upcoming-weeks',
                    label: 'Upcoming Weeks',
                    children: upcomingChildren,
                });
            }

            return nodes;
        },
    },

    mounted() {
        if (this.itemToOpen) {
            this.expandedKeys = {
                [this.itemToOpen]: true,
            };
        }
    },

    methods: {
        get,
        navTitleTooltip,
        showNavTitleTooltipOnFocus,
        hideNavTitleTooltipOnBlur,

        /**
         * Get the component name for a given item type
         */
        getComponentForItem(item) {
            if (!item || !item.type) return null;
            return pascalCase(item.type);
        },

        /**
         * @param tense
         * @return {Array|boolean}
         */
        weeks(tense) {
            const weeks = this.items.curriculum.weeks[tense];

            if (!weeks.length) return false;

            if (weeks.length === 1) return weeks;

            return [
                {
                    id: tense,
                    prerequisite: null,
                    section_type: 'topic',
                    text: this.getWeekContainerTitle(tense),
                    title: this.getWeekContainerTitle(tense),
                    type: 'section',
                    visible_at: null,
                    items: weeks,
                },
            ];
        },

        /**
         * @param tense
         * @return {string}
         */
        getWeekContainerTitle(tense) {
            return tense === 'past' ? 'Previous Weeks' : 'Upcoming Weeks';
        },

        onNodeSelect(node) {
            // Handle navigation via route
            if (node.route) {
                this.$router.push(node.route);
                return;
            }

            // Toggle expansion for nodes with children
            if (node.children && node.children.length > 0) {
                if (this.expandedKeys[node.key]) {
                    const newKeys = { ...this.expandedKeys };
                    delete newKeys[node.key];
                    this.expandedKeys = newKeys;
                } else {
                    this.expandedKeys = {
                        ...this.expandedKeys,
                        [node.key]: true,
                    };
                }
            }
        },

        convertNestedListToTreeNodes(items, parentKey = '') {
            if (!items || !Array.isArray(items)) return [];

            return items
                .filter(item => this.hasNestedListPermission(item))
                .map((item, index) => {
                    const key = `${parentKey}-${item.type || 'item'}-${item.id || index}`;
                    const node = {
                        key,
                        label: item.text || item.title || 'Untitled',
                        data: item,
                    };

                    // Handle items with children (recursive)
                    if (item.items && item.items.length > 0) {
                        node.children = this.convertNestedListToTreeNodes(item.items, key);
                    }

                    // Handle route navigation if item has route info
                    if (item.route_name) {
                        node.route = {
                            name: item.route_name,
                            params: item.route_params || {},
                        };
                        node.isActive = this.$route.name === item.route_name;
                    }

                    return node;
                });
        },

        hasNestedListPermission(item) {
            if (!item.permission) return true;

            const courseTemplate = CourseTemplate.query().with(['groups']).find(this.courseTemplateId);

            return (
                this.$can(item.permission.action, item.permission.subject) ||
                this.user.hasGroupPermissionWithinCourse(courseTemplate, item.permission.name)
            );
        },
    },
};
</script>

<style scoped lang="scss">
.p-tree {
    padding: 0;
    padding-block: 0.5rem;
}

:deep(.p-tree-node-content) {
    padding: 0;
    padding-block: 0.25rem;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

:deep(.p-tree-node-toggle-button[data-p-leaf='true']) {
    visibility: hidden;
}

:deep(.p-tree-node-label) {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

:deep(.p-tree-root-children),
:deep(.p-tree-node) {
    min-width: 0;
    max-width: 100%;
}

:deep(a) {
    min-width: 0;
    max-width: 100%;
    color: var(--text-color);
}

</style>
